/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Collapse, Form, Input, Row, Typography } from 'antd';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX, URL_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Text } = Typography;

const CallbackNodeForm = () => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage } = useBotLanguage();

  return (
    <Fragment>
      <Row gutter={16} className="mb-1">
        <Col xs={24}>
          <Form.Item
            name={['data', 'sendStoredData', 'callbackId']}
            hidden
          >
            <Input size="large" />
          </Form.Item>

          <Form.Item
            label={t('Callback URL')}
            name={['data', 'sendStoredData', 'url']}
            rules={[
              {
                required: true,
                message: t('URL cannot be blank.')
              },
              {
                validator: async (rule, value) => {
                  try {
                    if (value && !URL_REGEX.test(value)) {
                      throw new Error(rule.message);
                    }
                  } catch {
                    throw new Error(rule.message);
                  }
                },
                message: t('Please enter a valid URL')
              }]}
          >
            <Input
              placeholder={t('Enter the callback URL')}
              size="large"
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} wrap={false} className="mb-5">
        <Col flex="auto">
          <Row gutter={16}>
            <Col xs={12}>
              <Text>
                {t('Header name')}
              </Text>
            </Col>
            <Col xs={12}>
              <Text>
                {t('Header value')}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Form.List
            name={['data', 'sendStoredData', 'headers']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'key']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Header key')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Header value')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          {
                            fields.length > 1 ? (
                              <Col flex="none">
                                <Button
                                  icon={<DeleteIcon />}
                                  size="large"
                                  type="text"
                                  onClick={() => remove(name)}
                                  disabled={!isEditingPrimaryLanguage}
                                />
                              </Col>
                            ) : null
                          }
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add()}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new header')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>

      <Row gutter={16} className="mb-1">
        <Col xs={24}>
          <Collapse
            bordered={false}
            expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
          >
            <Collapse.Panel
              header={t('Advanced Settings')}
              forceRender
            >
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Callback ID')}
                  </Text>
                </Col>
              </Row>

              <Form.Item
                name={['data', 'sendStoredData', 'callbackId']}
              >
                <Input
                  placeholder={t('Enter the callback ID')}
                  size="large"
                  onKeyPress={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  maxLength={30}
                  disabled={!isEditingPrimaryLanguage}
                />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CallbackNodeForm;
